import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SCAPIShopperProductsSchemas, SCAPIShopperStoresSchemas } from 'src/types';
import { isCourseProduct } from '@/lib/product-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EventCourseDescriptionTab from './event-course-description-tab';
import EventCourseLocationTab from './event-course-location-tab';
import EventCourseLineUpTab from './event-course-lineup-tab';
import { parseEventLineUp } from './event-course-lineup';

/** Extendable set of tab ids; `menu` is not implemented yet. */
export type EventCourseTabId = 'description' | 'location' | 'chef';

interface EventCourseTabsProps {
    product: SCAPIShopperProductsSchemas['Product'];
    /** Store linked to the product via `c_eventStoreId`, feeding the "Location e contatti" tab. */
    eventStore: Promise<SCAPIShopperStoresSchemas['Store'] | undefined>;
}

/**
 * Tab strip below the hero for event/course PDPs. Exposes the "Descrizione" tab
 * (short + long description and the `large` image gallery) and the "Location e contatti"
 * tab (store address, contacts). Additional tabs (chef, menu) can be appended to `sections`.
 */
export default function EventCourseTabs({ product, eventStore }: EventCourseTabsProps) {
    const { t } = useTranslation('product');

    const sections = useMemo(() => {
        type Section = { id: EventCourseTabId; label: string; content: ReactNode };
        const lineUp = parseEventLineUp(product.c_eventLineUp);
        // Courses list "Docenti" (teachers), events list "Chef".
        const lineUpLabel = isCourseProduct(product) ? t('eventCourse.tabs.docenti') : t('eventCourse.tabs.chef');
        const lineUpSidebarLabel = isCourseProduct(product)
            ? t('eventCourse.lineUp.docentiSidebarLabel')
            : t('eventCourse.lineUp.chefSidebarLabel');
        return [
            {
                id: 'description',
                label: t('eventCourse.tabs.description'),
                content: <EventCourseDescriptionTab product={product} />,
            },
            {
                id: 'location',
                label: t('eventCourse.tabs.location'),
                content: <EventCourseLocationTab eventStore={eventStore} />,
            },
            ...(lineUp.length > 0
                ? [
                      {
                          id: 'chef' as const,
                          label: lineUpLabel,
                          content: <EventCourseLineUpTab lineUp={lineUp} sidebarLabel={lineUpSidebarLabel} />,
                      },
                  ]
                : []),
        ] satisfies Section[];
    }, [product, eventStore, t]);

    const defaultTab = sections[0]?.id ?? 'description';

    return (
        <Tabs defaultValue={defaultTab} className="gap-8 max-w-screen-2xl mx-auto w-full min-w-0">
            <TabsList
                scrollActiveTabOnMobile
                className="flex w-full max-w-full min-w-0 bg-transparent overflow-x-auto scrollbar-hide overflow-y-hidden px-0 justify-start gap-3">
                {sections.map((section) => (
                    <TabsTrigger
                        key={section.id}
                        value={section.id}
                        data-analytics-event="tab_click"
                        data-analytics-payload={JSON.stringify({ click_text: section.label })}
                        className="label-m shrink-0 bg-sand-95 text-neutral-10 rounded-full hover:bg-neutral-10 hover:text-neutral-white data-[state=active]:bg-neutral-10 data-[state=active]:text-neutral-white gap-0 w-fit md:flex-none md:w-fit">
                        {section.label}
                    </TabsTrigger>
                ))}
            </TabsList>
            {sections.map((section) => (
                <TabsContent key={section.id} value={section.id} className="w-full">
                    {section.content}
                </TabsContent>
            ))}
        </Tabs>
    );
}
